import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../components/ViewContainer";
import CountUp from "../../components/CountUp";
import { brand, fonts } from "../../themes/theme";
import { resolveByType } from "../../tenant/tenant";
import kpisData from "../../data/kpis.json";
import profilesData from "../../data/profiles.json";

/**
 * HU-5 — Señales conductuales · el diferenciador.
 * Cabecera + tarjeta comparativa (lo que ve tu CRM vs lo que añade Mential) +
 * lista "KPIs presentes en el perfil de fuga": los KPIs reales derivados del dato
 * del CRM, ordenados por presencia, con hover que expande el detalle de cada uno.
 */
function BehavioralView() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [openKey, setOpenKey] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState("all");

    // En movil el detalle se abre al pulsar y se mantiene hasta pulsar fuera.
    useEffect(() => {
        if (!isMobile || openKey == null) return;
        const close = () => setOpenKey(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [isMobile, openKey]);

    const crmSees = t("views.behavioral.crmSees", { returnObjects: true });
    const mentialAdds = t("views.behavioral.mentialAdds", { returnObjects: true });
    const kpis = resolveByType(kpisData);
    const profiles = resolveByType(profilesData).segments;
    const items = kpis.crm
        .map((item) => ({
            ...item,
            pct: selectedProfile === "all" ? item.pct : item.byProfile?.[selectedProfile] ?? item.pct,
            metricOverride: selectedProfile === "all" ? null : item.metricByProfile?.[selectedProfile],
        }))
        .sort((a, b) => b.pct - a.pct);

    return (
        <ViewContainer title={t("views.behavioral.title")}>
            <Box
                sx={{
                    flex: { xs: "0 0 auto", md: 1 },
                    minHeight: 0,
                    display: { xs: "flex", md: "grid" },
                    flexDirection: { xs: "column" },
                    gridTemplateColumns: { md: "2fr 1fr" },
                    gap: { xs: 1.5, md: 1.75 },
                }}
            >
                {/* KPIs presentes en el perfil de fuga — parte interactiva, mas ancho */}
                <Box
                    sx={{
                        flex: { xs: "0 0 auto", md: 1 },
                        minHeight: 0,
                        borderRadius: "16px",
                        border: "1px solid",
                        borderColor: brand.gray100,
                        bgcolor: brand.white,
                        p: { xs: 2, md: 2.5 },
                        display: "flex",
                        flexDirection: "column",
                        order: { xs: 2, md: 1 },
                    }}
                >
                    <Typography sx={{ fontFamily: fonts.display, fontSize: { xs: 16, md: 17 }, color: brand.navy, lineHeight: 1.2 }}>
                        {t("views.behavioral.sectionKick")}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: brand.gray500, lineHeight: 1.4, mt: 0.4 }}>
                        {t("views.behavioral.sectionSub")}
                    </Typography>

                    <ProfileFilterBar profiles={profiles} selected={selectedProfile} onSelect={setSelectedProfile} t={t} />

                    <Box sx={{ flex: { xs: "0 0 auto", md: 1 }, minHeight: 0, overflowY: { xs: "visible", md: "auto" }, display: "flex", flexDirection: "column", mt: { xs: 1.5, md: 1.5 } }}>
                        {items.map((item) => (
                            <KpiRow
                                key={item.key}
                                item={item}
                                t={t}
                                mobile={isMobile}
                                open={openKey === item.key}
                                onToggle={(e) => {
                                    e.stopPropagation();
                                    setOpenKey((k) => (k === item.key ? null : item.key));
                                }}
                            />
                        ))}
                    </Box>

                    <Typography
                        sx={{
                            fontSize: 10.5,
                            color: brand.gray500,
                            lineHeight: 1.5,
                            mt: 1.25,
                            pt: 1.25,
                            borderTop: "1px solid",
                            borderColor: brand.gray100,
                        }}
                    >
                        {t("views.behavioral.note")}
                    </Typography>
                </Box>

                {/* Comparativa · lo que ve tu CRM vs lo que añade Mential — solo lectura, columna mas estrecha */}
                <Box
                    sx={{
                        flexShrink: 0,
                        borderRadius: "16px",
                        border: "1px solid",
                        borderColor: brand.gray100,
                        bgcolor: brand.white,
                        overflow: "hidden",
                        order: { xs: 1, md: 2 },
                    }}
                >
                    <Box sx={{ p: { xs: 2, md: 2.25 } }}>
                        <ColTitle label={t("views.behavioral.crmSeesTitle")} tone="crm" />
                        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                            {crmSees.map((item, i) => (
                                <Bullet key={i} symbol="·" text={item} tone="crm" />
                            ))}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            p: { xs: 2, md: 2.25 },
                            bgcolor: "rgba(50,72,202,0.045)",
                            borderTop: "1px solid",
                            borderColor: brand.gray100,
                        }}
                    >
                        <ColTitle label={t("views.behavioral.mentialAddsTitle")} tone="app" />
                        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                            {mentialAdds.map((item, i) => (
                                <Bullet key={i} symbol="+" text={item} tone="app" />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ViewContainer>
    );
}

// Filtro por perfil de paciente (chips). De momento decorativo en el sentido
// de que los 8 KPIs no estan aun segmentados por perfil (ver disclaimer en la
// vista) — pero la seleccion es real y queda lista para cuando ese desglose
// exista.
function ProfileFilterBar({ profiles, selected, onSelect, t }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, mt: 1.25 }}>
            <Typography sx={{ fontSize: 11, color: brand.gray500, mr: 0.5 }}>{t("views.behavioral.profileFilterLabel")}:</Typography>
            <FilterChip active={selected === "all"} onClick={() => onSelect("all")}>
                {t("views.behavioral.profileFilterAll")}
            </FilterChip>
            {profiles.map((p) => (
                <FilterChip key={p.key} active={selected === p.key} onClick={() => onSelect(p.key)}>
                    {t(`views.profiles.segments.${p.key}.name`)}
                </FilterChip>
            ))}
        </Box>
    );
}

function FilterChip({ active, onClick, children }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                border: "1px solid",
                borderColor: active ? "primary.main" : brand.gray200,
                bgcolor: active ? "rgba(50,72,202,0.08)" : "transparent",
                color: active ? "primary.main" : brand.navyLight,
                borderRadius: "999px",
                px: 1.1,
                py: 0.4,
                fontFamily: fonts.body,
                fontSize: 11.5,
                fontWeight: active ? 500 : 400,
                cursor: "pointer",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": { borderColor: "primary.main" },
            }}
        >
            {children}
        </Box>
    );
}

function ColTitle({ label, tone }) {
    return (
        <Typography
            sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: tone === "app" ? brand.primary : brand.navyLight,
            }}
        >
            {label}
        </Typography>
    );
}

function Bullet({ symbol, text, tone }) {
    return (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography
                sx={{
                    fontFamily: fonts.display,
                    fontSize: 14,
                    lineHeight: 1.3,
                    color: tone === "app" ? brand.primary : brand.gray500,
                    width: 12,
                    flexShrink: 0,
                    textAlign: "center",
                }}
            >
                {symbol}
            </Typography>
            <Typography sx={{ fontSize: { xs: 13, md: 13.5 }, color: brand.navy, lineHeight: 1.35 }}>{text}</Typography>
        </Box>
    );
}

/** Fila de KPI: colapsada (nombre + barra + %). En hover se EXPANDE en la propia fila. */
function KpiRow({ item, t, mobile, open, onToggle }) {
    const base = `views.behavioral.crm.${item.key}`;
    const markers = t(`${base}.markers`).split(" · ");
    return (
        <Box
            onClick={mobile ? onToggle : undefined}
            sx={{
                borderBottom: "1px solid",
                borderColor: brand.gray100,
                borderRadius: "8px",
                cursor: mobile ? "pointer" : "default",
                transition: "background-color 0.2s ease",
                "&:last-of-type": { borderBottom: "none" },
                ...(open && { bgcolor: brand.gray50 }),
                "&:hover": { bgcolor: brand.gray50 },
                "&:hover .kpiDetail": { gridTemplateRows: "1fr", opacity: 1, transitionDelay: "0.2s" },
            }}
        >
            {/* Fila colapsada */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, md: 2.5 }, py: { xs: 0.7, md: 0.65 }, px: 1 }}>
                <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: brand.navy, lineHeight: 1.25, flex: 1, minWidth: 0 }} noWrap>
                    {t(`${base}.name`)}
                </Typography>
                <Box
                    sx={{
                        display: { xs: "none", sm: "block" },
                        width: { sm: 140, md: 220 },
                        height: 9,
                        borderRadius: 5,
                        bgcolor: brand.gray100,
                        overflow: "hidden",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            height: "100%",
                            width: `${item.pct}%`,
                            borderRadius: 5,
                            background: `linear-gradient(90deg, ${brand.primary}, ${brand.primaryLight})`,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontFamily: fonts.display,
                        fontSize: { xs: 16, md: 18 },
                        color: brand.navy,
                        lineHeight: 1,
                        width: { xs: 40, md: 46 },
                        textAlign: "right",
                        flexShrink: 0,
                        letterSpacing: "-0.02em",
                    }}
                >
                    <CountUp to={item.pct} suffix="%" />
                </Typography>
            </Box>

            {/* Detalle expandible (grid-rows 0fr → 1fr para animar el alto) */}
            <Box
                className="kpiDetail"
                sx={{
                    display: "grid",
                    gridTemplateRows: open ? "1fr" : "0fr",
                    opacity: open ? 1 : 0,
                    transition: "grid-template-rows 0.32s ease, opacity 0.28s ease",
                    px: 1,
                }}
            >
                <Box sx={{ overflow: "hidden", minHeight: 0 }}>
                    <Box
                        sx={{
                            mb: 1,
                            p: { xs: 1.25, md: 1.5 },
                            bgcolor: brand.white,
                            border: "1px solid",
                            borderColor: brand.gray100,
                            borderRadius: "12px",
                            borderLeft: `3px solid ${brand.primary}`,
                        }}
                    >
                        {/* Valor de ejemplo (protagonista) */}
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                            <Typography
                                sx={{ fontFamily: fonts.display, fontSize: { xs: 22, md: 25 }, color: brand.primary, lineHeight: 1, letterSpacing: "-0.02em" }}
                            >
                                {item.metricOverride || t(`${base}.metric`)}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: brand.navyLight, lineHeight: 1.3 }}>
                                {t(`${base}.metricLabel`)} · {t("views.behavioral.metricFoot")}
                            </Typography>
                        </Box>

                        {/* Explicación en lenguaje llano */}
                        <Typography sx={{ fontSize: 12, color: brand.navy, lineHeight: 1.4, mt: 0.75 }}>{t(`${base}.plain`)}</Typography>

                        {/* Marcadores que lo originan */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, mt: 0.9 }}>
                            <Typography sx={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em", color: brand.gray500, mr: 0.2 }}>
                                {t("views.behavioral.calcLabel")}
                            </Typography>
                            {markers.map((m, i) => (
                                <Typography
                                    key={i}
                                    sx={{
                                        fontSize: 10.5,
                                        lineHeight: 1.3,
                                        color: brand.navyLight,
                                        bgcolor: brand.gray50,
                                        border: "1px solid",
                                        borderColor: brand.gray100,
                                        borderRadius: "999px",
                                        px: 0.9,
                                        py: 0.3,
                                    }}
                                >
                                    {m}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default BehavioralView;
